/**
 * Zone volumes as geometry. Every shape in Annex A.2 except the whole-room case is symmetric about a
 * vertical axis through the source, so it is described by a profile (radius, height) revolved about
 * that axis. One description gives both the render mesh and an exact signed distance.
 */
import type { ZoneVolume } from '../engine/assess';
import type { Box, ReleaseInput, ReleaseForm, ShapeKind, Vec3 } from '../model/types';

/** [radius from the axis, height above ground], metres. Starts and ends on the axis (radius 0). */
export type ProfilePoint = [number, number];

export type Shape =
  | { kind: 'lathe'; shape: ShapeKind; axis: [number, number]; profile: ProfilePoint[] }
  | { kind: 'box'; box: Box };

const ARC_SEGMENTS = 32;

/** Quarter-ellipse arc from (r, z0) up to (0, z0 + h), horizontal semi-axis r, vertical h. */
function dome(r: number, z0: number, h: number): ProfilePoint[] {
  const pts: ProfilePoint[] = [];
  for (let i = 0; i <= ARC_SEGMENTS; i++) {
    const t = (i / ARC_SEGMENTS) * (Math.PI / 2);
    pts.push([r * Math.cos(t), z0 + h * Math.sin(t)]);
  }
  return pts;
}

/** A sphere of radius r centred at height zc, cut off at the ground. */
export function sphereProfile(r: number, zc: number): ProfilePoint[] {
  const pts: ProfilePoint[] = [];
  const bottom = zc - r;
  const start = bottom >= 0 ? -Math.PI / 2 : Math.asin(Math.max(-1, (0 - zc) / r));
  if (bottom < 0) pts.push([0, 0]);
  for (let i = 0; i <= ARC_SEGMENTS * 2; i++) {
    const t = start + (i / (ARC_SEGMENTS * 2)) * (Math.PI / 2 - start);
    pts.push([Math.max(0, r * Math.cos(t)), zc + r * Math.sin(t)]);
  }
  pts[pts.length - 1] = [0, zc + r];
  if (bottom >= 0) pts[0] = [0, bottom];
  return pts;
}

/** A vertical cylinder from z0 to z1 (both clamped at the ground). */
export function cylinderProfile(r: number, z0: number, z1: number): ProfilePoint[] {
  const lo = Math.max(0, z0);
  return [[0, lo], [r, lo], [r, z1], [0, z1]];
}

/** Cylinder from the ground up to the source, capped by a dome of height h above it (Fig. A.4a). */
export function columnProfile(r: number, sourceZ: number, h: number): ProfilePoint[] {
  const z = Math.max(0, sourceZ);
  return [[0, 0], [r, 0], ...dome(r, z, h)];
}

export interface ShapeInput {
  volume: ZoneVolume;
  sourcePosition: Vec3;
  release: ReleaseInput;
  releaseForm: ReleaseForm;
  override?: ShapeKind;
  /** The area's room box, for zones that fill the room. */
  roomBox?: Box;
}

/** The shape the engine suggests for a release (MD-018). */
export function suggestedShape(release: ReleaseInput, form: ReleaseForm): ShapeKind {
  if (release.model === 'pool') return 'pool-disc';
  if (form === 'heavy') return 'column';
  if (release.model === 'liquid') return 'drip-column';
  return 'sphere';
}

/** Builds the geometry for one zone volume, or undefined when its size isn't known yet. */
export function zoneShape(input: ShapeInput): Shape | undefined {
  const { volume, sourcePosition } = input;
  if (volume.roomFill) return input.roomBox ? { kind: 'box', box: input.roomBox } : undefined;
  const r = volume.horizontalM;
  const h = volume.verticalM ?? r;
  if (r === null || h === null || !(r > 0) || !(h > 0)) return undefined;
  const [x, y, z] = sourcePosition;
  const kind = input.override ?? suggestedShape(input.release, input.releaseForm);
  const lathe = (profile: ProfilePoint[]): Shape => ({ kind: 'lathe', shape: kind, axis: [x, y], profile });
  switch (kind) {
    case 'sphere':
      return lathe(sphereProfile(r, z));
    case 'vertical-cylinder':
      return lathe(cylinderProfile(r, z - h, z + h));
    case 'column':
      return lathe(columnProfile(r, z, h));
    case 'drip-column':
      return lathe(columnProfile(r, z, r));
    case 'pool-disc': {
      const poolRadius = input.release.model === 'pool' ? Math.sqrt(input.release.areaM2 / Math.PI) : 0;
      return lathe(cylinderProfile(poolRadius + r, 0, h));
    }
  }
}

export const SHAPE_LABEL: Record<ShapeKind, string> = {
  sphere: 'Sphere',
  'vertical-cylinder': 'Vertical cylinder',
  column: 'Column to ground',
  'drip-column': 'Drip column',
  'pool-disc': 'Pool disc',
};
